import { useState } from 'react'
import { notifications } from '@mantine/notifications'
import {
  usePreferenciaNotificacaoUpdate,
  usePreferenciasNotificacao,
} from '../../hooks/usePreferenciasNotificacao'
import { useTenantAtual } from '../../hooks/useTenant'
import {
  useTotpConfirmar,
  useTotpDesativar,
  useTotpSetup,
  useTrocarSenha,
  type TotpSetupResponse,
} from '../../hooks/useUsuarios'
import { useAuthStore } from '../../stores/authStore'
import styles from '../_shared/CrudPage.module.css'
import formStyles from '../_shared/CrudForm.module.css'

const PERFIL_LABEL: Record<string, string> = {
  admin_tenant: 'Administrador do Tenant',
  rt_mecanico: 'RT Mecânico',
  rt_qai: 'RT QAI',
  cliente: 'Cliente / Contratante',
  executora: 'Técnico / Executora',
}

function extractError(err: unknown): string {
  const e = err as { response?: { data?: Record<string, unknown> } }
  const data = e?.response?.data ?? {}
  for (const k of ['detail', 'senha_atual', 'senha_nova']) {
    const v = data[k]
    if (Array.isArray(v) && v.length) return `${k}: ${v[0]}`
    if (typeof v === 'string') return `${k}: ${v}`
  }
  return 'Erro ao trocar senha.'
}

export default function PerfilPage() {
  const user = useAuthStore((s) => s.user)
  const trocar = useTrocarSenha()
  const [senhaAtual, setSenhaAtual] = useState('')
  const [senhaNova, setSenhaNova] = useState('')
  const [confirmacao, setConfirmacao] = useState('')

  async function handleTrocarSenha(e: React.FormEvent) {
    e.preventDefault()
    if (senhaNova.length < 8) {
      notifications.show({ color: 'red', message: 'Nova senha deve ter ao menos 8 caracteres.' })
      return
    }
    if (senhaNova !== confirmacao) {
      notifications.show({ color: 'red', message: 'Senha nova e confirmação não conferem.' })
      return
    }
    try {
      await trocar.mutateAsync({ senha_atual: senhaAtual, senha_nova: senhaNova })
      notifications.show({ color: 'green', message: 'Senha trocada com sucesso.' })
      setSenhaAtual('')
      setSenhaNova('')
      setConfirmacao('')
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha',
        message: extractError(err),
      })
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Meu perfil</h1>
          <p className={styles.subtitle}>{user?.email}</p>
        </div>
      </header>

      <section
        style={{
          background: 'white',
          padding: '1.25rem',
          borderRadius: 8,
          marginBottom: '1rem',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>Dados</h2>
        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem 1rem' }}>
          <dt style={{ color: '#666' }}>Nome:</dt>
          <dd style={{ margin: 0 }}>{user?.nomeCompleto ?? '—'}</dd>
          <dt style={{ color: '#666' }}>E-mail:</dt>
          <dd style={{ margin: 0 }}>{user?.email ?? '—'}</dd>
          <dt style={{ color: '#666' }}>Perfil:</dt>
          <dd style={{ margin: 0 }}>
            {PERFIL_LABEL[user?.perfil ?? ''] ?? user?.perfil ?? '—'}
          </dd>
        </dl>
      </section>

      <section
        style={{
          background: 'white',
          padding: '1.25rem',
          borderRadius: 8,
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>Trocar senha</h2>
        <form onSubmit={handleTrocarSenha} className={formStyles.form}>
          <div className={formStyles.grid2}>
            <div className={`${formStyles.field} ${formStyles.span2}`}>
              <label htmlFor="senha-atual">Senha atual *</label>
              <input
                id="senha-atual"
                type="password"
                data-testid="senha-atual"
                value={senhaAtual}
                onChange={(e) => setSenhaAtual(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            <div className={formStyles.field}>
              <label htmlFor="senha-nova">Senha nova * (mín. 8)</label>
              <input
                id="senha-nova"
                type="password"
                data-testid="senha-nova"
                value={senhaNova}
                onChange={(e) => setSenhaNova(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            <div className={formStyles.field}>
              <label htmlFor="senha-confirmacao">Confirmar nova *</label>
              <input
                id="senha-confirmacao"
                type="password"
                data-testid="senha-confirmacao"
                value={confirmacao}
                onChange={(e) => setConfirmacao(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
          </div>

          <div className={formStyles.actions}>
            <button
              type="submit"
              className={formStyles.btnPrimary}
              disabled={trocar.isPending}
              data-testid="trocar-senha-submit"
            >
              {trocar.isPending ? 'Trocando...' : 'Trocar senha'}
            </button>
          </div>
        </form>
      </section>

      <Totp2FASection />

      <NotificacoesSection />
    </div>
  )
}

function NotificacoesSection() {
  const { data: prefs, isLoading } = usePreferenciasNotificacao()
  const { data: tenant } = useTenantAtual()
  const update = usePreferenciaNotificacaoUpdate()

  async function toggle(tipo: string, campo: 'canal_inapp' | 'canal_email', valor: boolean) {
    try {
      await update.mutateAsync({ tipo, [campo]: valor })
    } catch {
      notifications.show({ color: 'red', message: 'Falha ao salvar preferência.' })
    }
  }

  return (
    <section
      style={{
        background: 'white',
        padding: '1.25rem',
        borderRadius: 8,
        marginTop: '1rem',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      }}
      data-testid="perfil-notificacoes-section"
    >
      <h2 style={{ marginTop: 0, fontSize: '1rem' }}>🔔 Notificações</h2>
      <p style={{ color: '#666', fontSize: '0.85rem' }}>
        Escolha o que e por onde quer receber. Mudanças salvam automaticamente.
      </p>

      {tenant && !tenant.smtp_configurado && (
        <div
          style={{
            padding: '0.6rem 0.9rem',
            background: '#FFF8E1',
            border: '1px solid #FFD54F',
            borderRadius: 6,
            fontSize: '0.85rem',
            margin: '0.5rem 0 1rem',
          }}
        >
          ⚠️ E-mail desativado por falta de SMTP. Admin pode configurar em{' '}
          <a href="/configuracoes" style={{ fontWeight: 600 }}>
            Configurações → Integrações
          </a>
          .
        </div>
      )}

      {isLoading && <p style={{ color: '#666' }}>Carregando...</p>}

      {!isLoading && prefs && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #E0E0E0', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem 0.5rem 0.5rem 0' }}>Tipo</th>
              <th style={{ padding: '0.5rem', width: 120, textAlign: 'center' }}>In-app</th>
              <th style={{ padding: '0.5rem', width: 120, textAlign: 'center' }}>E-mail</th>
            </tr>
          </thead>
          <tbody>
            {prefs.map((p) => (
              <tr
                key={p.id}
                style={{ borderBottom: '1px solid #F5F5F5' }}
                data-testid={`pref-row-${p.tipo}`}
              >
                <td style={{ padding: '0.6rem 0.5rem 0.6rem 0' }}>{p.tipo_display}</td>
                <td style={{ padding: '0.6rem 0.5rem', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={p.canal_inapp}
                    aria-label={`In-app ${p.tipo_display}`}
                    data-testid={`pref-${p.tipo}-inapp`}
                    onChange={(e) => toggle(p.tipo, 'canal_inapp', e.target.checked)}
                  />
                </td>
                <td style={{ padding: '0.6rem 0.5rem', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={p.canal_email}
                    aria-label={`E-mail ${p.tipo_display}`}
                    data-testid={`pref-${p.tipo}-email`}
                    onChange={(e) => toggle(p.tipo, 'canal_email', e.target.checked)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

function Totp2FASection() {
  const setup = useTotpSetup()
  const confirmar = useTotpConfirmar()
  const desativar = useTotpDesativar()
  const [estagio, setEstagio] = useState<'inicial' | 'configurando' | 'ativo'>('inicial')
  const [setupData, setSetupData] = useState<TotpSetupResponse | null>(null)
  const [codigo, setCodigo] = useState('')
  const [senhaDesativar, setSenhaDesativar] = useState('')

  function extractError(err: unknown): string {
    const e = err as { response?: { data?: Record<string, unknown> } }
    const d = e?.response?.data ?? {}
    for (const k of ['totp', 'detail', 'senha_atual']) {
      const v = d[k]
      if (typeof v === 'string') return v
      if (Array.isArray(v) && v.length) return String(v[0])
    }
    return 'Erro no fluxo 2FA.'
  }

  async function handleSetup() {
    try {
      const r = await setup.mutateAsync()
      setSetupData(r)
      setEstagio('configurando')
    } catch (err) {
      notifications.show({ color: 'red', message: extractError(err) })
    }
  }

  async function handleConfirmar(e: React.FormEvent) {
    e.preventDefault()
    try {
      await confirmar.mutateAsync(codigo)
      notifications.show({ color: 'green', message: '2FA ativado com sucesso.' })
      setCodigo('')
      setSetupData(null)
      setEstagio('ativo')
    } catch (err) {
      notifications.show({ color: 'red', title: 'Falha', message: extractError(err) })
    }
  }

  async function handleDesativar(e: React.FormEvent) {
    e.preventDefault()
    try {
      await desativar.mutateAsync(senhaDesativar)
      notifications.show({ color: 'green', message: '2FA desativado.' })
      setSenhaDesativar('')
      setEstagio('inicial')
    } catch (err) {
      notifications.show({ color: 'red', title: 'Falha', message: extractError(err) })
    }
  }

  return (
    <section
      style={{
        background: 'white',
        padding: '1.25rem',
        borderRadius: 8,
        marginTop: '1rem',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      }}
      data-testid="perfil-2fa-section"
    >
      <h2 style={{ marginTop: 0, fontSize: '1rem' }}>
        Autenticação de 2 fatores (2FA)
      </h2>
      <p style={{ color: '#666', fontSize: '0.85rem' }}>
        Use um app authenticator (Google Authenticator, Authy, 1Password, etc).
        Após ativado, login passa a exigir um código de 6 dígitos além da senha.
      </p>

      {estagio === 'inicial' && (
        <button
          type="button"
          onClick={handleSetup}
          disabled={setup.isPending}
          data-testid="btn-2fa-setup"
          style={{
            padding: '0.5rem 1rem',
            background: '#1565C0',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          {setup.isPending ? 'Gerando...' : 'Ativar 2FA'}
        </button>
      )}

      {estagio === 'configurando' && setupData && (
        <form onSubmit={handleConfirmar}>
          <p style={{ fontSize: '0.85rem' }}>
            <strong>1.</strong> Escaneie o QR Code com seu app authenticator:
          </p>
          <img
            src={`data:image/png;base64,${setupData.qr_code_base64}`}
            alt="QR Code 2FA"
            style={{ maxWidth: 220, display: 'block', margin: '0.5rem 0' }}
          />
          <details style={{ fontSize: '0.8rem', margin: '0.5rem 0' }}>
            <summary>Não consigo escanear — mostrar secret</summary>
            <code
              style={{
                display: 'inline-block',
                padding: '0.4rem',
                background: '#F5F5F5',
                borderRadius: 4,
                marginTop: '0.25rem',
                fontFamily: 'monospace',
              }}
            >
              {setupData.secret}
            </code>
          </details>
          <p style={{ fontSize: '0.85rem' }}>
            <strong>2.</strong> Digite o código de 6 dígitos exibido no app:
          </p>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            pattern="[0-9]{6}"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
            data-testid="input-2fa-codigo"
            style={{
              padding: '0.5rem',
              fontSize: '1.1rem',
              letterSpacing: '0.3rem',
              fontFamily: 'monospace',
              width: 160,
            }}
            required
          />
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
            <button
              type="submit"
              disabled={confirmar.isPending || codigo.length !== 6}
              data-testid="btn-2fa-confirmar"
              style={{
                padding: '0.5rem 1rem',
                background: '#2E7D32',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {confirmar.isPending ? 'Confirmando...' : 'Confirmar e ativar'}
            </button>
            <button
              type="button"
              onClick={() => {
                setSetupData(null)
                setCodigo('')
                setEstagio('inicial')
              }}
              style={{
                padding: '0.5rem 1rem',
                background: 'white',
                color: '#666',
                border: '1px solid #888',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {estagio === 'ativo' && (
        <form onSubmit={handleDesativar}>
          <p style={{ color: '#2E7D32', fontWeight: 600 }}>✓ 2FA ativo</p>
          <p style={{ fontSize: '0.85rem', color: '#666' }}>
            Para desativar, confirme sua senha atual:
          </p>
          <input
            type="password"
            value={senhaDesativar}
            onChange={(e) => setSenhaDesativar(e.target.value)}
            data-testid="input-2fa-desativar-senha"
            style={{ padding: '0.5rem', minWidth: 260 }}
            required
            autoComplete="current-password"
          />
          <div style={{ marginTop: '0.5rem' }}>
            <button
              type="submit"
              disabled={desativar.isPending || !senhaDesativar}
              data-testid="btn-2fa-desativar"
              style={{
                padding: '0.5rem 1rem',
                background: 'white',
                color: '#C62828',
                border: '1px solid #C62828',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              {desativar.isPending ? 'Desativando...' : 'Desativar 2FA'}
            </button>
          </div>
        </form>
      )}
    </section>
  )
}
